/* global Sys */
Sys.ns("Sys");

/**
 * Wraps a DOM Element in a JS Object.
 *
 * @class Sys.Element
 * @extends Sys.Observable
 */
Sys.Element = {

    /**
     * Creates a Sys.Element that contains a DOM element.
     * All other properties (not listed below) on the object will be stored as an attribute on the element.
     *
     * @param {string|Object|HTMLElement} config The string, existing DOM element or object that defines the DOM element.
     * @param {string} [config.tag] The element tag type.
     * @param {string} [config.cls] The element class(es).
     * @param {string} [config.innerHTML] The elements inner html.
     * @param {string} [config.textContent] The elements inner text.
     * @param {string} [config.renderTo] An element id to render this element to.
     * @throws {Error}
     * @returns {void}
     */
    constructor: function(config) {
        var me = this;

        Sys.Element.superclass.constructor.apply(this, arguments);

        this.DOMEvents = {};
        this.el = this.setupElement(config);

        if (!this.el) {
            throw new Error("Invalid instantiation of Sys.Element, invalid input, needs element string or object");
        }

        // Adding default behaviour for webkitTransitionEnd event - if stopPropagation is not enabled by default
        // all webkitTransitions will bubble up to the document object by default and potentially trigger
        // webkitTransitionEnd listeners on higher levels of the application. For example - the settingsWindowEndToggling
        // event is fired using a listener on the uicard DOM object and would also fire on all reel transitions if this
        // default handler would not be present.
        this.el.addEventListener("transitionend", function(e) {
            if (me.transitionEnd) {
                me.transitionEnd.apply(me, arguments);
            }
            e.stopPropagation();
        }, false);

        if (document.getElementById(config.renderTo)) {
            document.getElementById(config.renderTo).appendChild(this.el);
        }
    },

    /**
     * @private
     * @param {string|Object|HTMLElement} config Configuration parameter.
     * @returns {?HTMLElement} DOM element.
     */
    setupElement: function(config) {
        this.el = null;

        if (!config) {
            return null;
        }
        else if (typeof config === "string") {
            this.el = document.createElement(config);
        }
        else if (config instanceof Element) {
            this.el = config;
        }
        else if (Sys.isObj(config)) {
            this.el = document.createElement(config.tag);

            Object
                .keys(config)
                .forEach(function(key) {
                    var value = config[key];

                    if (key === "cls") {
                        this.el.setAttribute("class", value);
                    }
                    else if (key === "innerHTML") {
                        this.el.innerHTML = value;
                    }
                    else if (key === "textContent") {
                        this.el.textContent = value;
                    }
                    else if (key === "onClick") {
                        this.el.addEventListener("click", value);
                    }
                    else if (key === "items") {
                        value.forEach(function(item) {
                            this.add(item);
                        }, this);
                    }
                    else if (key === "transitionend" && typeof value === "function") {
                        this.transitionEnd = value;
                    }
                    else if (key !== "renderTo" && key !== "tag") {
                        this.el.setAttribute(key, value);
                    }
                }, this);
        }

        return this.el;
    },

    /**
     * Returns the Sys.Element DOM element.
     *
     * @public
     * @returns {HTMLElement} The DOM element.
     */
    getEl: function() {
        return this.el;
    },

    /**
     * Returns the Sys.Element's DOM children.
     *
     * @protected
     * @returns {HTMLElement[]} The DOM elements as an array.
     */
    getChildren: function() {
        // returns a node list, we need to make a copy to not use a reference
        // since this can change later on when the children are modified
        return Array.prototype.slice.call(this.getEl().childNodes, 0);
    },

    /**
     * Appends a Sys.Element to another (this) Sys.Element.
     *
     * @protected
     * @param {Sys.Element} el The Sys.Element to add to this Sys.Element.
     * @returns {Sys.Element} The Sys.Element that was added.
     */
    add: function(el) {
        this.el.appendChild(el.getEl());
        el.parent = this;
        return el;
    },

    /**
     * Appends a Sys.Element to this Sys.Element before another Sys.Element.
     *
     * @protected
     * @param {Sys.Element} newEl The new Sys.Element to add.
     * @param {Sys.Element} beforeEl The Sys.Element to add before.
     * @returns {Sys.Element} The Sys.Element that was added.
     */
    addBefore: function(newEl, beforeEl) {
        this.el.insertBefore(newEl.getEl(), beforeEl.getEl());
        newEl.parent = this;
        return newEl;
    },

    /**
     * Appends an array of nodes to another (this) Sys.Element.
     *
     * @protected
     * @param {HTMLElement[]} children The DOM elements to add to this Sys.Element's HTMLElement.
     * @returns {void}
     */
    addChildren: function(children) {
        children.forEach(function(child) {
            this.el.appendChild(child);
        }, this);
    },

    /**
     * Removes a Sys.Element from another (this) Sys.Element.
     *
     * @protected
     * @param {Sys.Element} el The Sys.Element to remove from this Sys.Element.
     * @returns {Sys.Element} The Sys.Element that was removed.
     */
    remove: function(el) {
        var child = el.getEl();

        if (this.el === child.parentNode) {
            this.el.removeChild(child);
        }

        return el;
    },

    /**
     * Removes all DOM elements from this object.
     *
     * @protected
     * @returns {void}
     */
    removeAll: function() {
        while (this.el.firstChild) {
            this.el.removeChild(this.el.firstChild);
        }
    },

    /**
     * Adds a listener to an event of this object. Also adds event to DOM element.
     *
     * @protected
     * @param {string} event The event to listen to.
     * @param {Function} func The function executed when the event is dispatched.
     * @param {Object} [thisArg] The scope in which the event will be executed. If omitted, it defaults to the object that fired the event.
     * @param {Object} [options] Options object.
     * @param {boolean} [options.single=false] True if only execute listener once.
     * @returns {void}
     */
    addListener: function(event, func, thisArg, options) {
        var me = this,
            scope = thisArg || this.el,
            config = options || {},
            wrappedFn = function() {
                func.apply(scope);
            };

        Sys.Element.superclass.addListener.apply(this, arguments);

        if (config.single) {
            wrappedFn = function() {
                me.removeListener(event, func, scope);
                func.call(scope);
            };
        }

        this.DOMEvents[event] = this.DOMEvents[event] || [];

        this.DOMEvents[event].push({
            event: event,
            func: func,
            scope: scope,
            wrappedFn: wrappedFn
        });

        this.el.addEventListener(event, wrappedFn, false);
    },

    /**
     * Removes a listener from an event on this object.
     *
     * @protected
     * @param {string} event The event to listen to.
     * @param {Function} func The bound function executed when the event is dispatched.
     * @param {Object} [thisArg] The scope in which the event will be executed. If omitted, it defaults to the object that fired the event.
     * @returns {void}
     */
    removeListener: function(event, func, thisArg) {
        var scope = thisArg || this.el;

        Sys.Element.superclass.removeListener.apply(this, arguments);

        this.DOMEvents[event].forEach(function(evt, index) {
            if (evt.event === event && evt.func === func && evt.scope === scope) {
                this.DOMEvents[event].splice(index, 1);
                this.el.removeEventListener(event, evt.wrappedFn, false);
                return true;
            }
            return false;
        }, this);
    },

    /**
     * Returns the offset of the Sys element relative to the entire document.
     * Uses Sys.utils.getElOffset.
     *
     * @protected
     * @returns {Object} An object with properties "left" and "top" to indicate the offset in pixels.
     */
    getOffset: function() {
        return Sys.utils.getElOffset(this.el);
    },

    /**
     * Appends a Sys.Element as the first child onto the parent container (as opposed to Sys.Element.add() which always adds the element as the last child of the parent).
     *
     * @protected
     * @param {Sys.Element} newEl The Sys.Element to add to this Sys.Element.
     * @returns {Sys.Element} The Sys.Element that was added.
     */
    addAsFirst: function(newEl) {
        this.el.insertBefore(newEl.el, this.el.firstChild);
        return newEl;
    },

    /**
     * Checks if the element as a given class.
     *
     * @protected
     * @param {string} className The class to look for.
     * @returns {boolean} If element has class.
     */
    hasClass: function(className) {
        return this.el.classList.contains(className);
    },

    /**
     * Adds a class to the element.
     *
     * @protected
     * @param {string} className The class to add.
     * @returns {void}
     */
    addClass: function(className) {
        var names;

        if (className && className.length) {
            names = className.split(" ");

            if (names.length > 1) {
                names.forEach(function(name) {
                    this.el.classList.add(name);
                }.bind(this));
                return;
            }
            this.el.classList.add(className);
        }
    },

    /**
     * Removed a class from the element.
     *
     * @protected
     * @param {string} className The class to remove.
     * @returns {void}
     */
    removeClass: function(className) {
        var names;

        if (className && className.length) {
            names = className.split(" ");
            if (names.length > 1) {
                names.forEach(function(name) {
                    this.el.classList.remove(name);
                }.bind(this));
                return;
            }
            this.el.classList.remove(className);
        }
    },

    /**
     * Replaces a given class with another.
     *
     * @protected
     * @param {string} oldClass The class to remove.
     * @param {string} newClass The class to add.
     * @param {boolean} [force=false] Flag indicating if the new class should be added even if the old one was not found.
     * @returns {void}
     */
    replaceClass: function(oldClass, newClass, force) {
        if (this.el.classList.contains(oldClass)) {
            this.el.classList.remove(oldClass);
            this.el.classList.add(newClass);
        }
        else if (force) {
            this.el.classList.add(newClass);
        }
    },

    /**
     * Sets the CSS class string for the element.
     *
     * @protected
     * @param {string} classNames The CSS class string.
     * @returns {void}
     */
    setClass: function(classNames) {
        this.el.className = classNames;
    },

    /**
     * Add or remove one class from the element.
     *
     * @example
     *      var e = new Sys.Element("div");
     *      e.toggleClass("element--open");
     *
     * @protected
     * @param {string} className The CSS class string.
     * @param {boolean} [state] A boolean value to determine whether the class should be added or removed.
     * @returns {void}
     */
    toggleClass: function(className, state) {
        if (typeof state === "boolean") {
            if (state) {
                this.addClass(className);
            }
            else {
                this.removeClass(className);
            }
        }
        else if (this.hasClass(className)) {
            this.removeClass(className);
        }
        else {
            this.addClass(className);
        }
    }
};

Sys.Element.hasCls = Sys.Element.hasCSSClass = Sys.Element.hasClass;
Sys.Element.addCls = Sys.Element.addCSSClass = Sys.Element.addClass;
Sys.Element.removeCls = Sys.Element.removeCSSClass = Sys.Element.removeClass;
Sys.Element.replaceCls = Sys.Element.replaceCSSClass = Sys.Element.replaceClass;
Sys.Element.setCSSClassString = Sys.Element.setClass;
Sys.Element.toggleCls = Sys.Element.toggleClass;

Sys.Element = Sys.extend(Sys.Observable, Sys.Element, "Sys.Element");
